import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError, isFreemiumError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { UpgradePrompt } from "@/components/UpgradePrompt";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PatternsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiUpgrade, setAiUpgrade] = useState(null);
  const [directedUpgrade, setDirectedUpgrade] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");

  // Directed AI
  const [directedPrompt, setDirectedPrompt] = useState("");
  const [directedFocus, setDirectedFocus] = useState("");
  const [directedText, setDirectedText] = useState(null);
  const [directedLoading, setDirectedLoading] = useState(false);
  const [directedDate, setDirectedDate] = useState("");
  const [dayTrades, setDayTrades] = useState([]);
  const [selectedTradeIdx, setSelectedTradeIdx] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);

  // Threads
  const [threads, setThreads] = useState([]);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [expandedThread, setExpandedThread] = useState(null);

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/patterns/notes");
      setNotes(data);
    } catch (_e) { /* ignore */ }
  };

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/patterns/threads");
      setThreads(data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/journal/patterns");
      setData(data);
      setLoading(false);
      loadNotes();
      loadThreads();
    })();
  }, []);

  const onPickDate = async (dateStr) => {
    setDirectedDate(dateStr);
    setDayTrades([]);
    setSelectedTradeIdx([]);
    if (!dateStr) return;
    setDayLoading(true);
    try {
      const { data } = await api.get(`/journal/entries/${dateStr}`);
      const trades = data?.trades || [];
      setDayTrades(trades);
      setSelectedTradeIdx(trades.map((_, i) => i));
    } catch (_e) {
      setDayTrades([]);
    } finally {
      setDayLoading(false);
    }
  };

  const toggleTradeIdx = (i) => {
    setSelectedTradeIdx((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post("/patterns/notes", { text: noteText, tag: "self-observed" });
    setNoteText("");
    loadNotes();
  };
  const deleteNote = async (id) => {
    await api.delete(`/patterns/notes/${id}`);
    loadNotes();
  };
  const runDirected = async () => {
    if (!directedPrompt.trim()) return;
    setDirectedLoading(true);
    setDirectedUpgrade(null);
    try {
      const payload = { prompt: directedPrompt, focus: directedFocus || null };
      if (directedDate) {
        payload.date = directedDate;
        payload.trade_indices = selectedTradeIdx;
      }
      const { data } = await api.post("/journal/ai-directed", payload);
      setDirectedText(data.text);
      loadThreads();
    } catch (e) {
      if (isFreemiumError(e)) {
        setDirectedUpgrade(formatApiError(e.response?.data?.detail));
      } else {
        setDirectedText(formatApiError(e.response?.data?.detail) || e.message);
      }
    } finally { setDirectedLoading(false); }
  };

  const deleteThread = async (id) => {
    await api.delete(`/patterns/threads/${id}`);
    if (expandedThread === id) setExpandedThread(null);
    loadThreads();
  };

  const runAI = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiUpgrade(null);
    try {
      const { data } = await api.post("/journal/ai-patterns");
      setAiText(data.text || data.note);
    } catch (e) {
      if (isFreemiumError(e)) {
        setAiUpgrade(formatApiError(e.response?.data?.detail));
      } else {
        setAiError(formatApiError(e.response?.data?.detail) || e.message);
      }
    } finally {
      setAiLoading(false);
    }
  };

  const patterns = data?.patterns || [];

  return (
    <Layout>
      <div className="mb-8 fade-in max-w-3xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">
          &mdash; Behavioural signals
        </p>
        <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">
          Pattern Alerts
        </h1>
        <p className="text-[#A39A8E] mt-4">
          The system watches your journal for repeated actions and biases you may not notice
          yourself. Each flag is a chance to address a habit before it costs you.
        </p>
      </div>

      <section className="relative ai-glow border border-[#2E2A25] bg-[#0F0E0C] p-6 rounded-sm mb-8 max-w-3xl">
        <div className="relative z-10 flex items-baseline justify-between mb-4 gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— AI Confluences</p>
            <h2 className="font-head text-xl text-[#EBE5D9] mt-1">Deep pattern analysis</h2>
            <p className="text-sm text-[#A39A8E] mt-1 max-w-xl">
              Claude studies your full trade history — day-of-week edges, hourly loss clusters, pair/session/strategy interactions — and surfaces the repeating confluences that shape your P&amp;L.
            </p>
          </div>
          <button
            data-testid="ai-patterns-btn"
            onClick={runAI}
            disabled={aiLoading}
            className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-5 py-2 text-sm rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
          >
            {aiLoading ? "Analyzing…" : aiText ? "Re-run analysis" : "Analyze my patterns"}
          </button>
        </div>
        {aiError && <p className="relative z-10 text-[#B56258] text-sm font-mono">{aiError}</p>}
        {aiText ? (
          <div data-testid="ai-patterns-text" className="relative z-10 text-sm text-[#EBE5D9] leading-relaxed whitespace-pre-wrap">{aiText}</div>
        ) : (
          !aiError && <p className="relative z-10 text-sm text-[#A39A8E] italic">Click above to have Claude identify repeating confluences across all your trades.</p>
        )}
      </section>

      <section className="border border-[#2E2A25] bg-[#1A1815] p-6 rounded-sm mb-8 max-w-3xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— Your observations</p>
        <h2 className="font-head text-xl text-[#EBE5D9] mt-1">Manual notes</h2>
        <p className="text-sm text-[#A39A8E] mt-1">Something the system missed? Log it here.</p>
        <div className="flex gap-2 mt-4">
          <input data-testid="manual-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="e.g. I keep entering too early on Fridays" className="flex-1 bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm" />
          <button data-testid="manual-note-add" onClick={addNote} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-4 rounded-sm hover:bg-[#EBE5D9] transition-colors">Add</button>
        </div>
        <div className="mt-4 space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="flex justify-between items-start border border-[#2E2A25] p-3 rounded-sm">
              <p className="text-sm text-[#EBE5D9] whitespace-pre-wrap flex-1">{n.text}</p>
              <button onClick={() => deleteNote(n.id)} className="font-mono text-[10px] uppercase tracking-widest text-[#8C4A42] hover:text-[#B56258] ml-3">remove</button>
            </div>
          ))}
        </div>
      </section>

      <section className="relative ai-glow border border-[#2E2A25] bg-[#0F0E0C] p-6 rounded-sm mb-8 max-w-3xl">
        <p className="relative z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— Directed AI</p>
        <h2 className="relative z-10 font-head text-xl text-[#EBE5D9] mt-1">Ask Claude anything about your trading</h2>
        <p className="relative z-10 text-sm text-[#A39A8E] mt-1">Focus filter narrows the data Claude sees. Optionally scope to a single day.</p>

        <div className="relative z-10 mt-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <select data-testid="directed-focus" value={directedFocus} onChange={(e) => setDirectedFocus(e.target.value)} className="bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm">
              <option value="">All trades</option>
              <option value="wins">Wins only</option>
              <option value="losses">Losses only</option>
            </select>
            <input
              data-testid="directed-date"
              type="date"
              max={todayISO()}
              value={directedDate}
              onChange={(e) => onPickDate(e.target.value)}
              className="bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm"
            />
            {directedDate && (
              <button
                data-testid="directed-date-clear"
                onClick={() => onPickDate("")}
                className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9] px-2"
              >
                Clear day
              </button>
            )}
          </div>

          {directedDate && (
            <div className="border border-[#2E2A25] rounded-sm p-3 bg-[#1A1815]">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CFA66A] mb-2">Trades on {directedDate}</p>
              {dayLoading ? (
                <p className="text-xs text-[#A39A8E] font-mono">Loading trades…</p>
              ) : dayTrades.length === 0 ? (
                <p className="text-xs text-[#A39A8E] font-mono italic">No trades logged on this day.</p>
              ) : (
                <div className="space-y-1">
                  {dayTrades.map((t, i) => (
                    <label key={i} className="flex items-center gap-2 text-xs text-[#EBE5D9] font-mono cursor-pointer">
                      <input
                        data-testid={`directed-trade-check-${i}`}
                        type="checkbox"
                        checked={selectedTradeIdx.includes(i)}
                        onChange={() => toggleTradeIdx(i)}
                        className="accent-[#CFA66A]"
                      />
                      <span>{t.pair || "?"} · {(t.direction || "?").toUpperCase()} · pnl {t.pnl}</span>
                      {t.region_flag && (
                        <span className={`text-[10px] uppercase tracking-widest px-1 border rounded-sm ${
                          t.region_flag === "good" ? "border-[#7A9E80] text-[#7A9E80]" : "border-[#B56258] text-[#B56258]"
                        }`}>{t.region_flag}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <input data-testid="directed-prompt" value={directedPrompt} onChange={(e) => setDirectedPrompt(e.target.value)} placeholder="e.g. Where should I have exited these? How could I have traded smarter?" className="flex-1 bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm" />
            <button data-testid="directed-run" onClick={runDirected} disabled={directedLoading} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-4 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50">{directedLoading ? "…" : "Ask"}</button>
          </div>
        </div>

        {directedText && <div data-testid="directed-text" className="relative z-10 text-sm text-[#EBE5D9] mt-4 whitespace-pre-wrap leading-relaxed">{directedText}</div>}
        {directedUpgrade && <UpgradePrompt message={directedUpgrade} onDismiss={() => setDirectedUpgrade(null)} />}

        {/* Past coaching threads */}
        <div className="relative z-10 mt-6 border-t border-[#2E2A25] pt-4">
          <button
            data-testid="threads-toggle"
            onClick={() => setThreadsOpen((o) => !o)}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors"
          >
            {threadsOpen ? "▾" : "▸"} Past coaching threads ({threads.length})
          </button>
          {threadsOpen && (
            <div data-testid="threads-list" className="mt-3 space-y-2">
              {threads.length === 0 ? (
                <p className="text-xs text-[#A39A8E] italic font-mono">No threads yet. Ask Claude a question above to save your first one.</p>
              ) : threads.map((th, i) => (
                <div key={th.id} data-testid={`thread-item-${i}`} className="border border-[#2E2A25] rounded-sm bg-[#1A1815]">
                  <div className="flex items-center justify-between p-3 gap-3">
                    <button
                      onClick={() => setExpandedThread(expandedThread === th.id ? null : th.id)}
                      className="flex-1 text-left"
                    >
                      <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                        {(th.created_at || "").slice(0, 10)}
                        {th.date && ` · scoped to ${th.date}`}
                        {th.focus && ` · ${th.focus}`}
                      </p>
                      <p className="text-sm text-[#EBE5D9] mt-1 truncate">{th.prompt}</p>
                    </button>
                    <button
                      data-testid={`thread-delete-${i}`}
                      onClick={() => deleteThread(th.id)}
                      className="font-mono text-[10px] uppercase tracking-widest text-[#8C4A42] hover:text-[#B56258]"
                    >
                      remove
                    </button>
                  </div>
                  {expandedThread === th.id && (
                    <div data-testid={`thread-response-${i}`} className="border-t border-[#2E2A25] p-3 text-sm text-[#EBE5D9] whitespace-pre-wrap leading-relaxed">
                      {th.response}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {loading ? (
        <div className="text-center text-[#A39A8E] py-16 font-mono text-sm">
          Scanning your entries...
        </div>
      ) : patterns.length === 0 ? (
        <div className="border border-dashed border-[#2E2A25] bg-[#1A1815] p-10 text-center rounded-sm">
          <p className="font-head text-xl text-[#EBE5D9] mb-2">All clear.</p>
          <p className="text-sm text-[#A39A8E] max-w-md mx-auto">
            {data?.note ||
              "No recurring patterns detected in your recent trades. Keep journaling — patterns emerge over time."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {patterns.map((p, i) => (
            <div
              key={i}
              data-testid={TID.patternItem(i)}
              className={`border p-5 rounded-sm ${
                p.severity === "high"
                  ? "border-[#8C4A42]/40 bg-[#8C4A42]/5"
                  : "border-[#CFA66A]/30 bg-[#CFA66A]/5"
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border rounded-sm shrink-0 ${
                    p.severity === "high"
                      ? "border-[#8C4A42] text-[#B56258]"
                      : "border-[#CFA66A] text-[#CFA66A]"
                  }`}
                >
                  {p.severity}
                </div>
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">
                    {p.type.replace("_", " ")}
                  </p>
                  <h3 className="font-head text-lg text-[#EBE5D9] mt-1">{p.title}</h3>
                  <p className="text-sm text-[#A39A8E] mt-2 leading-relaxed">{p.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
